import { useRef, useState } from 'react'
import { useQueryClient } from "react-query"

import { DocumentArrowUpIcon } from '@heroicons/react/24/outline'
import { Alert, Button, Checkbox, Modal } from 'flowbite-react'

import { useUploadDevolucaoEmMassa } from '@/hooks/queries/use-get-devolucoes'
import EnvioSucessoDevolucaoEmMassaModal from './envio-sucesso-devolucao-em-massa-modal.component'
import EnvioErroDevolucaoEmMassaModal from './envio-erro-devolucao-em-massa-modal.component'
import EnvioErroGenericoDevolucaoEmMassaModal from './envio-erro-generico-devolucao-em-massa-modal.component'
import Loader from '@/components/Loader'
import { useRegistrosContext } from '@/contexts/registros.context'
import { QueryKey } from '@/configs/queryKeys'

interface IResumoPedidoModalProps {
  onClose: () => void
  isOpen: boolean
}

const AcoesUploadModal: React.FC<IResumoPedidoModalProps> = ({
  onClose,
  isOpen
}: IResumoPedidoModalProps) => {
  const [isEnvioSucessoDevolucaoEmMassaModalOpen, setIsEnvioSucessoDevolucaoEmMassaModalOpen] =
    useState<boolean>(false)
  const [isEnvioErroDevolucaoEmMassaModalOpen, setIsEnvioErroDevolucaoEmMassaModalOpen] =
    useState<boolean>(false)
  const [isEnvioErroGenericoDevolucaoEmMassaModalOpen, setIsEnvioErroGenericoDevolucaoEmMassaModalOpen] =
    useState<boolean>(false)
  const [processErrors, setProcessErrors] = useState(false)
  const [fileUploaded, setFileUploaded] = useState(null)
  const [errorUploaded, setErrorUploaded] = useState(null)
  const { userData } = useRegistrosContext()
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const queryClient = useQueryClient()

  const paramsUploadDevolucaoEmMassa: any = {
    ignorarErros: processErrors,
    arquivo: fileUploaded,
    idEmpregador: userData?.authenticatedAccountId ?? '',
    idUsuario: userData?.userId ?? '',
    usuario: userData?.userName ?? ''
  }

  const { refetch: uploadDevolucaoEmMassa, isLoading: isLoadingUpload } = useUploadDevolucaoEmMassa({
    params: paramsUploadDevolucaoEmMassa
  })

  const handleCheckboxProcessErrors = (element: React.ChangeEvent<HTMLInputElement>) => {
    setProcessErrors(element?.target?.checked)
  }

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0]
    if (file) {
      const maxSize = 10 * 1024 * 1024; // 10MB
      const allowedExtensions = ['xls', 'xlsx'];
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      if (!allowedExtensions.includes(fileExtension)) {
        setErrorUploaded("O arquivo selecionado deve ser do tipo .xls ou .xlsx.");
        setFileUploaded(null);
        return;
      }
      if (file.size > maxSize) {
        setErrorUploaded("O arquivo selecionado excede o tamanho máximo permitido de 10MB.")
        setFileUploaded(null)
        return;
      }
      setFileUploaded(file)
    }

    setTimeout(handleSubmit, 500);
  }

  const handleSubmit = () => {
    uploadDevolucaoEmMassa()
      .then(response => {
        if (response?.isSuccess) {
          setIsEnvioSucessoDevolucaoEmMassaModalOpen(true)
        } else if (response.error === 'Solicitação não realizada!') {
          setIsEnvioErroGenericoDevolucaoEmMassaModalOpen(true)
        } else {
          setIsEnvioErroDevolucaoEmMassaModalOpen(true)
        }
      })
      .catch(() => {
        setIsEnvioErroGenericoDevolucaoEmMassaModalOpen(true)
      }).finally(() => {
        onClose()
        setFileUploaded(null)
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
        queryClient.invalidateQueries(QueryKey.registrosList)
      })
  }

  return (
    <>
      <Modal
        popup
        show={isOpen}
        onClose={onClose}
        className='flex items-center'
        size='md'
      >
        <Modal.Header data-testid='acoes-devolucoes-modal-content'>
          <p className='text-center font-bold p-3'>Devolução em lote</p>
        </Modal.Header>
        <Modal.Body>
          {isLoadingUpload ? (
            <div className='p-20'>
              <Loader size={'xl'} />
            </div>
          ) : (
            <div className='flex flex-col items-center justify-center'>
              <div
                id='download-partial-resume'
                className='flex flex-col items-center'
              >
                <div className='self-center pb-4'>
                  <p className='text-center text-quaternary font-semibold pb-3 text-xl'>
                    Enviar planilha
                  </p>
                  <p className='text-center text-gray-600 text-sm'>
                    Utilize o botão <strong>Enviar planilha</strong> para enviar
                    a planilha para a fila de processamento.
                  </p>
                </div>
                <div className='self-center pb-5'>
                  <Alert color='warning' rounded className='p-3'>
                    <div className='flex items-center justify-center text-xs'>
                      <Checkbox
                        name='processErrors'
                        className='mr-3'
                        onChange={handleCheckboxProcessErrors}
                        data-testid='checkbox-process-errors'
                      />
                      <p>Processar a planilha mesmo se houver erros na planilha
                        (os trabalhadores com erros não serão processados).</p>
                    </div>
                  </Alert>
                </div>
                <div className='self-center'>
                  {fileUploaded && (
                    <Alert color='success' rounded className='p-3 mb-3 text-xs'>
                      {fileUploaded?.name} - {Math.round(fileUploaded.size / 1024)} KB
                    </Alert>
                  )}
                  {errorUploaded && (
                    <Alert color='red' rounded className='p-3 mb-3 text-xs'>
                      {errorUploaded}
                    </Alert>
                  )}
                </div>
                <div className='self-center'>
                  <Button
                    type='button'
                    color='light'
                    onClick={handleButtonClick}
                    className='btn btn-primary'
                    id='complete-resume-button'
                    data-testid='complete-resume-button'
                    disabled={isLoadingUpload}
                  >
                    {!isLoadingUpload && <DocumentArrowUpIcon className='h-5 w-5 mr-1' />}
                    <span>Enviar planilha</span>
                  </Button>
                  <input
                    type="file"
                    onChange={handleFileChange}
                    accept=".xls,.xlsx"
                    multiple={false}
                    style={{ display: 'none' }}
                    ref={fileInputRef}
                  />
                </div>
              </div>
            </div>
          )}
          <div className='w-full flex justify-center items-center pt-5'>
            <Button
              onClick={onClose}
              className='btn btn-secondary flex justify-center bg-white border-primary-dark text-primary-dark rounded w-fit'
              id='close-button'
              data-testid='close-button'
            >
              Fechar
            </Button>
          </div>
        </Modal.Body>
      </Modal>
      <EnvioSucessoDevolucaoEmMassaModal
        isOpen={isEnvioSucessoDevolucaoEmMassaModalOpen}
        onClose={() => setIsEnvioSucessoDevolucaoEmMassaModalOpen(false)}
      />
      <EnvioErroDevolucaoEmMassaModal
        isOpen={isEnvioErroDevolucaoEmMassaModalOpen}
        onClose={() => setIsEnvioErroDevolucaoEmMassaModalOpen(false)}
      />
      <EnvioErroGenericoDevolucaoEmMassaModal
        isOpen={isEnvioErroGenericoDevolucaoEmMassaModalOpen}
        onClose={() => setIsEnvioErroGenericoDevolucaoEmMassaModalOpen(false)}
      />
    </>
  )
}

export default AcoesUploadModal
